// Typy subelementów (CMS) + karuzela produktów dla aktywnego typu (WiseCarousel = wrapper nagłówka + Slider).

import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import classnames from 'classnames';
import { Slide, Slider } from 'pure-react-carousel';

import { useRWD } from 'hooks';
import {
  useGetHomeSubelementsTypes,
  useGetHomeSubelementItems,
  useGetHomeSubelementItemsMutation
} from 'api';
import type { IProductListItem } from 'api/types';
import { Container } from 'components/controls';
import WiseCarousel, { type WiseCarouselTab } from 'pages/wise/WiseProduct/components/WiseCarousel';

import { WiseHomeSubelementsProductSlider } from './WiseHomeSubelementsProductSlider';
import styles from 'theme/components/wise/WiseHomeSubelements/WiseHomeSubelements.module.scss';

const ITEMS_PER_PAGE = 6;
const SCOPES_LIST =
  'BasicProductData|ImageFormattedProductData|UnitProductData|StockFormattedProductData';

const CARD_W = 260;
const CARD_H = 465;
const MAX_VISIBLE_SLIDES = 5;
const CARD_GAP = 8;
/** Tymczasowy placeholder przy fetchu (zakładka / doładowanie strony). */
const FETCH_PLACEHOLDER_SLIDES = 5;
/** Minimalny czas szkieletu po zmianie zakładki / resecie listy - mniej „migania” między szkieletem a kartą. */
const MIN_TAB_SKELETON_MS = 320;

function cardsPerRow(widthPx: number): number {
  if (widthPx <= 0) return 1;
  const slot = CARD_W + CARD_GAP;
  return Math.max(1, Math.floor((widthPx + CARD_GAP) / slot));
}

function mergePriceRows(
  list: IProductListItem[],
  full?: { items?: IProductListItem[] }
): IProductListItem[] {
  const priced = full?.items;
  if (!priced?.length) return list;
  return list.map((row) => {
    const extra = priced.find((p) => p.id === row.id);
    return extra ? { ...row, ...extra } : row;
  });
}

export const WiseHomeSubelements = () => {
  const [activeTypeId, setActiveTypeId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<IProductListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const lastPriceRequestKey = useRef('');
  const lastMergedPageRef = useRef(0);
  const skeletonMinUntilRef = useRef(0);
  const blockRef = useRef<HTMLDivElement>(null);
  const [fitsInRow, setFitsInRow] = useState<number | null>(null);
  /** Po zmianie typu / resecie nie pokazuj nawet cache z RQ, dopóki bieżące zapytanie się nie zakończy (`listFetching`). */
  const [hideListUntilFetched, setHideListUntilFetched] = useState(false);
  /** Doładowanie kolejnej strony - nie opieraj się tylko na `listFetching` (cache może wrócić synchronicznie). */
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [slideSyncRevision, setSlideSyncRevision] = useState(0);
  const slideIndexByTabRef = useRef<Record<string, number>>({});
  const prevPlaceholderSkeletonsRef = useRef(false);

  const bumpSlideSync = useCallback(() => {
    setSlideSyncRevision((n) => n + 1);
  }, []);

  const { isMobile } = useRWD();
  const listEnabled = activeTypeId != null && activeTypeId !== '';

  const { data: typesData } = useGetHomeSubelementsTypes({ page: 1, limit: 999 });

  useEffect(() => {
    const first = typesData?.items?.[0];
    if (first == null || activeTypeId != null) return;
    setActiveTypeId(first.id);
  }, [typesData, activeTypeId]);

  const {
    data: pageData,
    isLoading: listLoading,
    isFetching: listFetching,
    isError: listError
  } = useGetHomeSubelementItems(
    activeTypeId ?? '',
    { page, limit: ITEMS_PER_PAGE, scopes: SCOPES_LIST },
    {
      enabled: listEnabled,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  const { data: priceData, mutate: fetchPrices, isLoading: priceLoading } =
    useGetHomeSubelementItemsMutation(activeTypeId ?? '');

  const resetListState = useCallback(() => {
    setPage(1);
    setItems([]);
    setHasMore(false);
    lastPriceRequestKey.current = '';
    lastMergedPageRef.current = 0;
    skeletonMinUntilRef.current = Date.now() + MIN_TAB_SKELETON_MS;
    setHideListUntilFetched(true);
    setIsLoadingMore(false);
  }, []);

  /** Koniec „świeżej” odpowiedzi dla bieżącego klucza - wtedy dopiero pokazuj listę (nawet z cache), żeby uniknąć migotania starych danych pod szkieletem. Po spełnieniu warunków czekamy jeszcze do `MIN_TAB_SKELETON_MS` od `resetListState`, żeby szkielet nie znikał w ułamku sekundy. */
  useEffect(() => {
    if (!hideListUntilFetched || !listEnabled) return;
    if (listError) {
      setHideListUntilFetched(false);
      return;
    }
    if (listFetching || pageData === undefined) return;

    const release = () => setHideListUntilFetched(false);
    const now = Date.now();
    const wait = skeletonMinUntilRef.current - now;
    if (wait <= 0) {
      release();
      return;
    }
    const id = window.setTimeout(release, wait);
    return () => window.clearTimeout(id);
  }, [hideListUntilFetched, listEnabled, listFetching, pageData, listError]);

  useEffect(() => {
    if (listError) setIsLoadingMore(false);
  }, [listError]);

  useEffect(() => {
    resetListState();
  }, [activeTypeId, resetListState]);

  useEffect(() => {
    bumpSlideSync();
  }, [activeTypeId, bumpSlideSync]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      resetListState();
      const match = typesData?.items?.find((t) => String(t.id) === tabId);
      setActiveTypeId(match != null ? match.id : tabId);
    },
    [typesData?.items, resetListState]
  );

  useEffect(() => {
    if (!listEnabled || !pageData?.items) return;
    if (lastMergedPageRef.current === page) {
      setIsLoadingMore(false);
      return;
    }

    const chunk = pageData.items as IProductListItem[];

    setItems((prev) => {
      if (page === 1) {
        lastMergedPageRef.current = page;
        return chunk;
      }
      const seen = new Set(prev.map((p) => p.id));
      const added = chunk.filter((p) => !seen.has(p.id));
      lastMergedPageRef.current = page;
      return [...prev, ...added];
    });

    const pages = pageData.total_pages || 0;
    setHasMore(page < pages);
    setIsLoadingMore(false);
  }, [listEnabled, activeTypeId, pageData, page]);

  useEffect(() => {
    if (items.length === 0) return;

    const key = items
      .map((p) => p.id)
      .filter((id, i, a) => a.indexOf(id) === i)
      .sort((a, b) => a - b)
      .join('|');

    if (lastPriceRequestKey.current === key) return;
    lastPriceRequestKey.current = key;

    fetchPrices({ scopes: 'PriceProductData', productIds: key, page: 1, limit: 999 });
  }, [items, fetchPrices]);

  const loadNextPage = useCallback(() => {
    if (isLoadingMore || listFetching || !hasMore) return;
    setIsLoadingMore(true);
    setPage((p) => p + 1);
  }, [isLoadingMore, listFetching, hasMore]);

  const rows = useMemo(() => mergePriceRows(items, priceData), [items, priceData]);
  const count = rows.length;
  const listGateBusy = hideListUntilFetched && listFetching;
  const displayCount = listGateBusy ? 0 : count;
  const tabKey = String(activeTypeId ?? '');

  const typesOk = Boolean(typesData?.items?.length);
  const busyList = listLoading || listFetching;
  const showPlaceholderSkeletons =
    (hideListUntilFetched && listEnabled) ||
    (displayCount < 1 && busyList) ||
    (isLoadingMore && !listGateBusy && count > 0);

  useEffect(() => {
    if (prevPlaceholderSkeletonsRef.current && !showPlaceholderSkeletons) {
      bumpSlideSync();
    }
    prevPlaceholderSkeletonsRef.current = showPlaceholderSkeletons;
  }, [showPlaceholderSkeletons, bumpSlideSync]);

  useLayoutEffect(() => {
    const el = blockRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => setFitsInRow(cardsPerRow(el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count]);

  const visibleSlides = useMemo(() => {
    if (displayCount < 1) return 1;
    if (isMobile) return 1;
    return Math.min(MAX_VISIBLE_SLIDES, displayCount, Math.max(1, fitsInRow ?? MAX_VISIBLE_SLIDES));
  }, [displayCount, isMobile, fitsInRow]);

  const tabs: WiseCarouselTab[] = useMemo(
    () => typesData?.items.map((t) => ({ id: String(t.id), label: t.name })) ?? [],
    [typesData]
  );

  const clampedSyncSlide = useMemo(() => {
    const raw = slideIndexByTabRef.current[tabKey] ?? 0;
    if (count < 1) return 0;
    const vis = visibleSlides;
    const maxStart = Math.max(0, count - vis);
    return Math.max(0, Math.min(raw, maxStart));
  }, [tabKey, count, visibleSlides, slideSyncRevision]);

  const slidesTotal = showPlaceholderSkeletons
    ? 0
    : displayCount > 0
      ? displayCount
      : 1;
  /** Placeholder: zawsze FETCH_PLACEHOLDER_SLIDES szkieletów w DOM; `visibleSlides` tylko ile widać naraz (mobile = 1). */
  const placeholderVisibleSlides = isMobile
    ? 1
    : Math.min(FETCH_PLACEHOLDER_SLIDES, MAX_VISIBLE_SLIDES);
  const slidesVisible = showPlaceholderSkeletons
    ? placeholderVisibleSlides
    : displayCount > 0
      ? visibleSlides
      : 1;
  const showArrows = !isMobile && displayCount > 0 && !showPlaceholderSkeletons;

  const handleSlideIndexChange = useCallback(
    (index: number) => {
      slideIndexByTabRef.current[tabKey] = index;
    },
    [tabKey]
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Home-components-Tabs-WiseHomeSubelements'
      )}>
      {typesOk && activeTypeId != null && (
        <Container>
          <div ref={blockRef} className={styles.sliderBlock}>
            <WiseCarousel
              className={styles.carousel}
              tabs={tabs}
              activeTabId={String(activeTypeId)}
              onTabChange={handleTabChange}
              totalSlides={slidesTotal}
              visibleSlides={slidesVisible}
              naturalSlideWidth={CARD_W}
              naturalSlideHeight={CARD_H}
              relaxHeight
              showNavArrows={showArrows}
              disableKeyboard
              disableAnimation={priceLoading}
              isLoading={showPlaceholderSkeletons}
              skeletonTotalSlides={
                showPlaceholderSkeletons ? FETCH_PLACEHOLDER_SLIDES : undefined
              }
              syncSlideIndex={
                showPlaceholderSkeletons || displayCount < 1 ? undefined : clampedSyncSlide
              }
              slideSyncRevision={
                showPlaceholderSkeletons || displayCount < 1 ? undefined : slideSyncRevision
              }>
              {displayCount > 0 && !showPlaceholderSkeletons ? (
                <WiseHomeSubelementsProductSlider
                  key={String(activeTypeId)}
                  products={rows}
                  itemsPerSlide={visibleSlides}
                  onSlideIndexChange={handleSlideIndexChange}
                  onLoadMore={loadNextPage}
                  hasMore={hasMore}
                />
              ) : showPlaceholderSkeletons ? null : (
                <Slider>
                  <Slide index={0} className={styles.carouselSlide}>
                    <div className={styles.placeholderSlide} />
                  </Slide>
                </Slider>
              )}
            </WiseCarousel>
          </div>
        </Container>
      )}
    </div>
  );
};

export default WiseHomeSubelements;
